import { httpClient } from '@/lib/httpClient';

export interface TaxonomyGetResponse {
  id?: string;
  type: string;
  groupName: string;
  categoryName: string;
  property: string;
  description: string;
  examples?: string;
  filterValues?: string[];
  order: number;
}

export interface CreateCompleteVersionDto {
  data: TaxonomyGetResponse[];
  taxonomiesToRemove: string[];
  version: string;
}

export interface EditTaxonomyPropertiesDto {
  id: string;
  examples: string;
}

export interface TaxonomiesUpdatedResponse {
  version: string;
  data: EditTaxonomyPropertiesDto[];
}

export interface TaxonomiesVersionsResponse {
  versions: string[];
}

export interface SyncTaxonomyDefaultDto {
  version: string;
}

export interface SyncTaxonomyDefaultResponse {
  success: boolean;
  message: string;
}

export interface CloneTaxonomyDto {
  sourceCustomerId: number;
  sourceVersion: string;
  targetCustomerId: number;
}

export interface CloneTaxonomyResponse {
  version: string;
  customerId: number;
}

class TaxonomiesService {
  constructor() {}

  async getTaxonomiesByVersion(version: string) {
    const { data } = await httpClient.get<string[]>(
      `/v1/taxonomies/versions/${version}/categories`,
    );

    return data;
  }

  async getTaxonomyPropertiesByVersionAndCategory(
    version: string,
    category: string,
  ) {
    const { data } = await httpClient.get<TaxonomyGetResponse[]>(
      `/v1/taxonomies/versions/${version}/categories/${category}/properties`,
    );

    return data;
  }

  async updateTaxonomiesByVersion(
    taxonomiesUpdatedResponse: TaxonomiesUpdatedResponse,
  ) {
    const { data } = await httpClient.post<TaxonomiesUpdatedResponse>(
      `/v1/taxonomies/versions`,
      taxonomiesUpdatedResponse,
    );
    return data;
  }

  async getTaxonomiesVersions() {
    const { data } = await httpClient.get<string[]>('/v1/taxonomies/versions');
    return data;
  }

  async getTaxonomyCurrentPropertiesByCategoryName(categoryName: string) {
    const { data } = await httpClient.get<TaxonomyGetResponse[]>(
      `/v1/taxonomies/current/properties/category/${categoryName}`,
    );
    return data;
  }

  async getAdminTaxonomiesVersions(customerId: number) {
    const { data } = await httpClient.get<string[]>(
      customerId == 0
        ? `/v1/taxonomies/default/versions`
        : `/v1/taxonomies/customer/${customerId}/versions/`,
    );
    return data;
  }

  async createAdminTaxonomy(
    customerId: number,
    createCompleteVersion: CreateCompleteVersionDto,
  ) {
    const { data } = await httpClient.post<CreateCompleteVersionDto>(
      customerId == 0
        ? `/v1/taxonomies/default/versions`
        : `/v1/taxonomies/customer/${customerId}/versions`,
      createCompleteVersion,
    );
    return data;
  }
  async getAdminTaxonomiesByVersion(customerId: number, version: string) {
    const { data } = await httpClient.get<string[]>(
      customerId == 0
        ? `/v1/taxonomies/default/versions/${version}/categories`
        : `/v1/taxonomies/customer/${customerId}/versions/${version}/categories`,
    );
    return data;
  }

  async getAdminTaxonomyPropertiesByVersionAndCategory(
    customerId: number,
    version: string,
    category: string,
  ) {
    const { data } = await httpClient.get<TaxonomyGetResponse[]>(
      customerId == 0
        ? `/v1/taxonomies/default/versions/${version}/categories/${category}/properties`
        : `/v1/taxonomies/customer/${customerId}/versions/${version}/categories/${category}/properties`,
    );
    return data;
  }

  async syncTaxonomyDefault(syncTaxonomyDefaultDto: SyncTaxonomyDefaultDto) {
    const { data } = await httpClient.post<SyncTaxonomyDefaultResponse>(
      `/v1/taxonomies/default/sync`,
      syncTaxonomyDefaultDto,
    );
    return data;
  }

  async cloneTaxonomy(cloneTaxonomyDto: CloneTaxonomyDto) {
    const { data } = await httpClient.post<CloneTaxonomyResponse>(
      `/v1/taxonomies/clone`,
      cloneTaxonomyDto,
    );
    return data;
  }
}

export default new TaxonomiesService();
